import { Sheet, SheetContent } from "@/components/ui/Sheet";
import Logo from "@/assets/logo.png";
import CampaignIcon from "@/assets/campaign.png";
import SettingsIcon from "@/assets/settings.png";
import useAuthStore, { SidebarItem } from "@/store/authStore";
import { useLocation, useNavigate } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";

interface SidebarLinksProps {
  title: string;
  icon: string;
  items: SidebarItem[];
  activeRoute: string;
}

function SidebarLinks({ title, icon, items, activeRoute }: SidebarLinksProps) {
  const navigate = useNavigate();

  return (
    <div>
      <div className="flex flex-col justify-start px-6 py-5">
        <div className="flex gap-6 items-center">
          <img src={icon} className="w-[15px] h-[15px]" alt={`${title} Icon`} />
          <p className="text-xl font-medium">{title}</p>
        </div>
        <div className="flex flex-col justify-start mt-4 ml-11">
          {items.map(({ title, route }) => {
            const isActive = activeRoute === route;
            return (
              <div
                key={title}
                onClick={() => navigate(route)}
                className={`cursor-pointer mb-2 group flex items-center relative ${
                  isActive ? "font-semibold text-[#EA33C0]" : ""
                }`}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    navigate(route);
                  }
                }}
              >
                <p
                  className={`text-black font-medium text-base text-left pr-2 transition-colors ${
                    isActive ? "text-[#EA33C0]" : "group-hover:text-[#EA33C0]"
                  }`}
                >
                  {title}
                </p>
                {isActive && (
                  <span className="absolute -left-4 top-1 h-5 w-1.5 bg-[#EA33C0] rounded-lg" />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

interface SidebarProps {
  mobileOpen?: boolean;
  setMobileOpen?: (open: boolean) => void;
}

export default function Sidebar({ mobileOpen, setMobileOpen }: SidebarProps) {
  const { user, sidebarData, user_role, media_agency_information } =
    useAuthStore(
      useShallow((state) => ({
        user: state.user,
        sidebarData: state.sidebar,
        user_role: state.user_role,
        media_agency_information: state.media_agency_information,
      }))
    );

  const navigate = useNavigate();
  const location = useLocation();
  const activeRoute = location.pathname;

  const campaigns: SidebarItem[] = [
    { title: "Dashboard", route: "/dashboard" },
    { title: "Campaign List", route: "/campaign-list" },
  ];

  const agencyLogo = media_agency_information?.logo_url_signed || Logo;

  const userInitial = user?.first_name?.[0]?.toUpperCase() || "U";

  const sidebarCore = (
    <>
      {/* Agency logo */}
      <div onClick={() => navigate("/profile")} className="cursor-pointer">
        {user_role === "Global Admin" ? (
          <img
            src={Logo}
            className="w-full h-24 object-contain"
            alt="Global Admin Logo"
          />
        ) : (
          <img
            src={agencyLogo}
            className="w-full h-24 object-contain"
            alt="Agency Logo"
          />
        )}
      </div>

      <div className="bg-[#EA33C0] h-[1.5px]" />

      {/* User info */}
      <div className="pt-4 px-3 py-3">
        <div
          onClick={() => navigate("/profile")}
          className="flex gap-3 cursor-pointer"
        >
          <div className="flex justify-center items-center w-[52px] h-[52px] rounded-full bg-[#C72BA4] border border-[#EA33C0] overflow-hidden">
            {user?.profile_picture ? (
              <img
                src={user.profile_picture}
                alt="User avatar"
                className="w-full h-full object-cover"
              />
            ) : (
              <p className="text-3xl font-medium text-white">{userInitial}</p>
            )}
          </div>
          <div className="flex flex-col items-start">
            <p className="text-base font-semibold">
              {user?.first_name + " " + user?.last_name || "Test User"}
            </p>
            <p className="text-base">{user_role || "N.A"}</p>
          </div>
        </div>

        {/* Notifications link */}
        <div className="flex flex-col justify-start px-4">
          <div className="flex flex-col justify-start mt-4 ml-11">
            <a href="/notification" className="mb-2 active">
              <p className="text-black font-medium text-base text-left">
                Notifications
              </p>
            </a>
          </div>
        </div>
      </div>

      <div className="bg-[#EA33C0] h-[1.5px]" />

      {/* Campaigns & Settings */}
      {user_role !== "Global Admin" && (
        <>
          <SidebarLinks
            title="Campaigns"
            icon={CampaignIcon}
            items={campaigns}
            activeRoute={activeRoute}
          />
          {sidebarData.length !== 0 && (
            <div className="bg-[#EA33C0] h-[1.5px]" />
          )}
        </>
      )}
      {sidebarData.length !== 0 && (
        <SidebarLinks
          title="Settings"
          icon={SettingsIcon}
          items={sidebarData}
          activeRoute={activeRoute}
        />
      )}
    </>
  );

  // Desktop sidebar + mobile sheet
  return (
    <>
      <aside className="sticky top-0 w-[300px] h-screen overflow-y-auto bg-white shadow-2xl hidden md:block z-10">
        {sidebarCore}
      </aside>
      <Sheet open={Boolean(mobileOpen)} onOpenChange={setMobileOpen}>
        <SheetContent
          side="left"
          className="p-0 w-64 max-w-full bg-white overflow-y-auto"
        >
          <div className="pt-4">{sidebarCore}</div>
        </SheetContent>
      </Sheet>
    </>
  );
}
